import React from 'react';
import {
    WHEEL_ZOOM_STEP,
    ZOOM_STEP,
    clampZoom,
    isTypingTarget,
    mountedCarousels,
    type CarouselState,
    type ContainerSize,
    type Point,
    type ZoomPanView
} from './imageCarouselShared';

export const useContainerSize = (ref: React.RefObject<HTMLDivElement | null>): ContainerSize => {
    const [size, setSize] = React.useState<ContainerSize>({ width: 0, height: 0 });

    React.useEffect(() => {
        const el = ref.current;
        if (!el) return;

        let timeoutId: number;
        const ro = new ResizeObserver((entries) => {
            clearTimeout(timeoutId);
            timeoutId = window.setTimeout(() => {
                const r = entries[0]?.contentRect;
                if (r) setSize({ width: Math.floor(r.width), height: Math.floor(r.height) });
            }, 100);
        });

        ro.observe(el);
        setSize({ width: el.clientWidth, height: el.clientHeight });

        return () => {
            clearTimeout(timeoutId);
            ro.disconnect();
        };
    }, [ref]);

    return size;
};

const useZoomState = () => {
    const [zoom, setZoom] = React.useState<number>(1);
    const [offset, setOffset] = React.useState<Point>({ x: 0, y: 0 });

    const resetView = React.useCallback(() => {
        setZoom(1);
        setOffset({ x: 0, y: 0 });
    }, []);

    const zoomIn = React.useCallback(() => {
        setZoom((z) => clampZoom(z + ZOOM_STEP));
    }, []);

    const zoomOut = React.useCallback(() => {
        setZoom((z) => {
            const next = clampZoom(z - ZOOM_STEP);
            if (next <= 1) setOffset({ x: 0, y: 0 });
            return next;
        });
    }, []);

    return { zoom, setZoom, offset, setOffset, resetView, zoomIn, zoomOut };
};

interface PanArgs {
    containerRef: React.RefObject<HTMLDivElement | null>;
    containerSize: ContainerSize;
    zoom: number;
    offset: Point;
    setOffset: React.Dispatch<React.SetStateAction<Point>>;
}

const usePanHandlers = ({ containerRef, containerSize, zoom, offset, setOffset }: PanArgs) => {
    const [isPanning, setIsPanning] = React.useState<boolean>(false);
    const panStartRef = React.useRef<{
        x: number;
        y: number;
        startX: number;
        startY: number;
    } | null>(null);

    const clampOffset = React.useCallback(
        (x: number, y: number) => {
            if (zoom <= 1) return { x: 0, y: 0 };
            const maxX = ((zoom - 1) * containerSize.width) / 2;
            const maxY = ((zoom - 1) * containerSize.height) / 2;
            return {
                x: Math.max(-maxX, Math.min(maxX, x)),
                y: Math.max(-maxY, Math.min(maxY, y))
            };
        },
        [zoom, containerSize.width, containerSize.height]
    );

    const onMouseDown = (e: React.MouseEvent) => {
        containerRef.current?.focus({ preventScroll: true });
        if (zoom <= 1) return;
        e.preventDefault();
        setIsPanning(true);
        panStartRef.current = { x: offset.x, y: offset.y, startX: e.clientX, startY: e.clientY };
    };

    const onMouseMove = (e: React.MouseEvent) => {
        if (!isPanning || !panStartRef.current) return;
        const dx = e.clientX - panStartRef.current.startX;
        const dy = e.clientY - panStartRef.current.startY;
        setOffset(clampOffset(panStartRef.current.x + dx, panStartRef.current.y + dy));
    };

    const endPan = () => {
        setIsPanning(false);
        panStartRef.current = null;
    };

    return { isPanning, onMouseDown, onMouseMove, endPan };
};

export const useZoomPan = (
    containerRef: React.RefObject<HTMLDivElement | null>,
    containerSize: ContainerSize
): ZoomPanView => {
    const { zoom, setZoom, offset, setOffset, resetView, zoomIn, zoomOut } = useZoomState();
    const { isPanning, onMouseDown, onMouseMove, endPan } = usePanHandlers({
        containerRef,
        containerSize,
        zoom,
        offset,
        setOffset
    });

    const onWheel = React.useCallback(
        (e: React.WheelEvent) => {
            if (!(e.ctrlKey || e.metaKey)) return;
            e.preventDefault();
            const delta = e.deltaY;
            setZoom((z) => {
                const next = clampZoom(z + (delta > 0 ? -WHEEL_ZOOM_STEP : WHEEL_ZOOM_STEP));
                if (next <= 1) setOffset({ x: 0, y: 0 });
                return next;
            });
        },
        [setZoom, setOffset]
    );

    const onDoubleClick = React.useCallback(() => {
        if (zoom === 1) setZoom(2);
        else resetView();
    }, [zoom, setZoom, resetView]);

    return {
        zoom,
        offset,
        isPanning,
        zoomIn,
        zoomOut,
        resetView,
        handlers: {
            onMouseDown,
            onMouseMove,
            onMouseUp: endPan,
            onMouseLeave: endPan,
            onWheel,
            onDoubleClick
        }
    };
};

export const useImagePreload = (urls: string[]) => {
    const [loadedImages, setLoadedImages] = React.useState<Set<string>>(new Set());
    const [loadingImages, setLoadingImages] = React.useState<Set<string>>(new Set());
    const checkedImages = React.useRef<Set<string>>(new Set());

    React.useEffect(() => {
        const settle = (url: string, loaded: boolean) => {
            if (loaded) setLoadedImages((prev) => new Set(prev).add(url));
            setLoadingImages((prev) => {
                const next = new Set(prev);
                next.delete(url);
                return next;
            });
        };

        urls.forEach((url) => {
            if (checkedImages.current.has(url)) return;
            checkedImages.current.add(url);

            const img = new Image();
            img.onload = () => settle(url, true);
            img.onerror = () => settle(url, false);
            img.src = url;

            if (img.complete && img.naturalWidth > 0) settle(url, true);
            else setLoadingImages((prev) => new Set(prev).add(url));
        });
    }, [urls]);

    return React.useCallback(
        (url: string | undefined): boolean =>
            !!url && loadingImages.has(url) && !loadedImages.has(url),
        [loadingImages, loadedImages]
    );
};

interface CarouselKeysArgs {
    containerRef: React.RefObject<HTMLDivElement | null>;
    isFullscreen: boolean;
    exitFullscreen: () => void;
    goPrev: () => void;
    goNext: () => void;
}

const useCarouselKeys = ({
    containerRef,
    isFullscreen,
    exitFullscreen,
    goPrev,
    goNext
}: CarouselKeysArgs) => {
    React.useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        mountedCarousels.add(el);
        return () => {
            mountedCarousels.delete(el);
        };
    }, [containerRef]);

    React.useEffect(() => {
        if (isFullscreen) containerRef.current?.focus({ preventScroll: true });
    }, [isFullscreen, containerRef]);

    const ownsArrowKeys = React.useCallback(() => {
        const el = containerRef.current;
        if (!el) return false;
        if (isFullscreen) return true;
        const active = document.activeElement;
        if (active && el.contains(active)) return true;
        return mountedCarousels.size === 1 && !isTypingTarget(active);
    }, [isFullscreen, containerRef]);

    React.useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isFullscreen) {
                // Host panels listen on `window` too; only the innermost layer may close per press.
                e.stopImmediatePropagation();
                e.preventDefault();
                exitFullscreen();
                return;
            }
            if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
            if (!ownsArrowKeys()) return;
            if (e.key === 'ArrowLeft') goPrev();
            else goNext();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isFullscreen, exitFullscreen, goPrev, goNext, ownsArrowKeys]);
};

interface CarouselStateArgs {
    containerRef: React.RefObject<HTMLDivElement | null>;
    imageCount: number;
    resetView: () => void;
    resetKey?: string | number;
    index?: number;
    onIndexChange?: (index: number) => void;
}

const useCarouselIndex = (
    controlledIndex: number | undefined,
    onIndexChange: ((index: number) => void) | undefined
) => {
    const [ownIndex, setOwnIndex] = React.useState(0);
    const isControlled = controlledIndex !== undefined;

    const setIndex = React.useCallback(
        (next: number) => {
            if (!isControlled) setOwnIndex(next);
            onIndexChange?.(next);
        },
        [isControlled, onIndexChange]
    );

    return { index: isControlled ? controlledIndex : ownIndex, setIndex, setOwnIndex };
};

export const useCarouselState = ({
    containerRef,
    imageCount,
    resetView,
    resetKey,
    index: controlledIndex,
    onIndexChange
}: CarouselStateArgs): CarouselState => {
    const { index, setIndex, setOwnIndex } = useCarouselIndex(controlledIndex, onIndexChange);
    const [isFullscreen, setIsFullscreen] = React.useState(false);
    const [showThumbnails, setShowThumbnails] = React.useState(true);

    // Clamped during render: a shrinking `images` would otherwise paint one frame on a stale index.
    const safeIndex = imageCount > 0 ? Math.min(index, imageCount - 1) : 0;
    const hasMultiple = imageCount > 1;

    const step = React.useCallback(
        (delta: number) => {
            if (imageCount < 2) return;
            setIndex((Math.min(index, imageCount - 1) + delta + imageCount) % imageCount);
            resetView();
        },
        [index, imageCount, resetView, setIndex]
    );

    const goPrev = React.useCallback(() => step(-1), [step]);
    const goNext = React.useCallback(() => step(1), [step]);
    const exitFullscreen = React.useCallback(() => setIsFullscreen(false), []);
    const toggleFullscreen = React.useCallback(() => setIsFullscreen((prev) => !prev), []);
    const toggleThumbnails = React.useCallback(() => setShowThumbnails((v) => !v), []);

    useCarouselKeys({ containerRef, isFullscreen, exitFullscreen, goPrev, goNext });

    // A controlled host owns the index, so a reset must not report a jump to the first image.
    React.useEffect(() => {
        setOwnIndex(0);
        resetView();
        setShowThumbnails(true);
    }, [imageCount, resetKey, resetView, setOwnIndex]);

    return {
        safeIndex,
        setIndex,
        isFullscreen,
        toggleFullscreen,
        showThumbnails,
        toggleThumbnails,
        goPrev,
        goNext,
        hasMultiple
    };
};

export const useValidImages = (images: string[]): string[] =>
    React.useMemo(
        () => (images || []).filter((img) => typeof img === 'string' && img.trim()),
        [images]
    );

// Misaligned thumbnails would scrub to the wrong photo, so require a 1:1 match.
export const useThumbStrip = (
    thumbnails: string[] | undefined,
    imageCount: number
): string[] | null =>
    React.useMemo(() => {
        if (!thumbnails || thumbnails.length !== imageCount) return null;
        return thumbnails.every((t) => typeof t === 'string' && t.trim()) ? thumbnails : null;
    }, [thumbnails, imageCount]);
